import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Badge, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faDollarSign,
  faBoxes,
  faClock,
  faPlay,
  faArrowLeft,
  faUsers,
  faEye,
  faHeart,
  faShare,
  faChartLine,
  faTrophy,
  faTimes,
  faToggleOn,
  faToggleOff
} from "@fortawesome/free-solid-svg-icons";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";

function LiveMarathon() {
  const { liveId } = useParams();
  const history = useHistory();

  // Decode base64 string to object (Unicode safe)
  const live = useMemo(() => {
    try {
      const decoded = decodeURIComponent(escape(atob(liveId)));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding liveId:', error);
      return null;
    }
  }, [liveId]);
  
  // Audio refs for sound notifications
  const orderSoundRef = useRef(null);
  const revenueSoundRef = useRef(null);
  const stockSoundRef = useRef(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ type: '', value: '', title: '', icon: null, color: '' });
  
  // Mock data for UI demonstration
  const [liveData, setLiveData] = useState({
    id: live?.id || '1',
    title: live?.pageName || 'Super Flash Sale Marathon 🔥',
    status: live?.status || 'LIVE',
    createdAt: live?.createdAt || moment().subtract(2, 'hours').toISOString(),
    description: live?.description || 'Amazing deals with up to 70% off on selected items!',
    viewers: 1247,
    likes: 892,
    shares: 156
  });
  
  const [stats, setStats] = useState({
    totalOrders: 342,
    totalRevenue: 15750000,
    totalStock: 156,
    totalViews: 8934,
    conversionRate: 12.5,
    avgOrderValue: 46052
  });
  
  const [currentTime, setCurrentTime] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [isPublicSharingEnabled, setIsPublicSharingEnabled] = useState(true);

  // Mock recent orders for demonstration
  const [recentOrders, setRecentOrders] = useState([
    { id: 1, customerName: 'ນາງ ສົມຈິດ', product: 'iPhone 15 Pro', amount: 25000000, time: '2 minutes ago' },
    { id: 2, customerName: 'ທ້າວ ບຸນມີ', product: 'Samsung Galaxy S24', amount: 18500000, time: '5 minutes ago' },
    { id: 3, customerName: 'ນາງ ມາລີ', product: 'MacBook Air M3', amount: 32000000, time: '8 minutes ago' },
    { id: 4, customerName: 'ທ້າວ ສົມພອນ', product: 'iPad Pro', amount: 22000000, time: '12 minutes ago' },
    { id: 5, customerName: 'ນາງ ຄຳພອນ', product: 'AirPods Pro', amount: 4500000, time: '15 minutes ago' }
  ]);

  // Sound notification function
  const playNotificationSound = useCallback((type) => {
    try {
      let audioRef;
      switch (type) {
        case 'order':
          audioRef = orderSoundRef;
          break;
        case 'revenue':
          audioRef = revenueSoundRef;
          break;
        case 'stock':
          audioRef = stockSoundRef;
          break;
        default:
          return;
      }
      
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    } catch (error) {
      console.log('Sound notification error:', error);
    }
  }, []);

  // Auto-update statistics simulation (replacing useSubscription with interval)
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setStats(prevStats => {
        const newStats = { ...prevStats };
        let hasChanges = false;
        
        // Randomly update orders (increase more often than decrease)
        if (Math.random() < 0.7) { // 70% chance to update orders
          const orderChange = Math.random() < 0.8 ? 
            Math.floor(Math.random() * 5) + 1 : // 80% chance to increase
            -Math.floor(Math.random() * 2) - 1; // 20% chance to decrease
          
          newStats.totalOrders = Math.max(0, prevStats.totalOrders + orderChange);
          if (orderChange !== 0) {
            hasChanges = true;
            playNotificationSound('order');
          }
        }
        
        // Update revenue based on orders
        if (Math.random() < 0.6) { // 60% chance to update revenue
          const revenueChange = Math.random() < 0.85 ? 
            Math.floor(Math.random() * 500000) + 100000 : // 85% chance to increase
            -Math.floor(Math.random() * 200000) - 50000; // 15% chance to decrease
          
          newStats.totalRevenue = Math.max(0, prevStats.totalRevenue + revenueChange);
          if (revenueChange !== 0) {
            hasChanges = true;
            playNotificationSound('revenue');
          }
        }
        
        // Update stock (decrease more often than increase)
        if (Math.random() < 0.5) { // 50% chance to update stock
          const stockChange = Math.random() < 0.3 ? 
            Math.floor(Math.random() * 10) + 1 : // 30% chance to increase (restocking)
            -Math.floor(Math.random() * 5) - 1; // 70% chance to decrease (sales)
          
          newStats.totalStock = Math.max(0, prevStats.totalStock + stockChange);
          if (stockChange !== 0) {
            hasChanges = true;
            playNotificationSound('stock');
          }
        }
        
        // Update average order value
        if (newStats.totalOrders > 0) {
          newStats.avgOrderValue = Math.floor(newStats.totalRevenue / newStats.totalOrders);
        }
        
        return hasChanges ? newStats : prevStats;
      });
    }, 3000); // Update every 3 seconds

    return () => clearInterval(updateInterval);
  }, [playNotificationSound]);

  // Handle card click to show modal
  const handleCardClick = useCallback((type) => {
    let modalInfo = {};
    
    switch (type) {
      case 'orders':
        modalInfo = {
          type: 'orders',
          value: stats.totalOrders.toLocaleString(),
          title: 'Total Orders',
          icon: faShoppingCart,
          color: 'linear-gradient(135deg, #ff6b6b, #ee5a24)'
        };
        break;
      case 'revenue':
        modalInfo = {
          type: 'revenue',
          value: formatCurrency(stats.totalRevenue),
          title: 'Total Revenue',
          icon: faDollarSign,
          color: 'linear-gradient(135deg, #2ecc71, #27ae60)'
        };
        break;
      case 'stock':
        modalInfo = {
          type: 'stock',
          value: stats.totalStock.toLocaleString(),
          title: 'Items in Stock',
          icon: faBoxes,
          color: 'linear-gradient(135deg, #3498db, #2980b9)'
        };
        break;
      case 'duration':
        modalInfo = {
          type: 'duration',
          value: getTimeDifference(),
          title: 'Live Duration',
          icon: faClock,
          color: 'linear-gradient(135deg, #9b59b6, #8e44ad)'
        };
        break;
      default:
        return;
    }
    
    setModalData(modalInfo);
    setShowModal(true);
  }, [stats]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getTimeDifference = () => {
    if (!liveData?.createdAt) return "00:00:00";
    
    const startTime = moment(liveData.createdAt);
    const duration = moment.duration(currentTime.diff(startTime));
    
    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const seconds = duration.seconds().toString().padStart(2, '0');
    
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('lo-LA', {
      style: 'currency',
      currency: 'LAK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: "20px 0"
    }}>
      <Container fluid style={{ maxWidth: "1400px" }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <Button
                  variant="link"
                  onClick={() => history.goBack()}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    borderRadius: "50%",
                    width: "45px",
                    height: "45px",
                    color: "white",
                    marginRight: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease"
                  }}
                  className="p-0"
                >
                  <FontAwesomeIcon icon={faArrowLeft} size="lg" />
                </Button>
                <div>
                  <h1 style={{ 
                    color: "white", 
                    margin: 0, 
                    fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                    fontWeight: "800",
                    textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                  }}>
                    🏃‍♂️ Live Marathon Dashboard
                  </h1>
                  <p style={{ 
                    color: "rgba(255,255,255,0.9)", 
                    margin: "5px 0 0 0",
                    fontSize: "clamp(1rem, 2.5vw, 1.3rem)",
                    fontWeight: "500"
                  }}>
                    {liveData?.title}
                  </p>
                </div>
              </div>
              
              <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 gap-lg-3">
                {/* First row on mobile: Live badge and viewer count */}
                <div className="d-flex align-items-center gap-2 gap-md-3 mb-2 mb-lg-0">
                  <Badge 
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      color: "white",
                      padding: "8px 16px",
                      fontSize: "clamp(0.8rem, 2vw, 1rem)",
                      borderRadius: "20px",
                      fontWeight: "600",
                      backdropFilter: "blur(10px)",
                      whiteSpace: "nowrap"
                    }}
                  >
                    <FontAwesomeIcon icon={faPlay} className="me-1 me-md-2" />
                    🔴 LIVE
                  </Badge>
                  
                  <div className="d-flex align-items-center text-white">
                    <FontAwesomeIcon icon={faEye} className="me-1 me-md-2" />
                    <span style={{ fontSize: "clamp(0.8rem, 2vw, 1rem)", fontWeight: "600" }}>
                      {formatNumber(liveData.viewers)}
                    </span>
                  </div>
                </div>

                {/* Second row on mobile: Control buttons */}
                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 w-100 w-lg-auto">
                  {/* Public Sharing Toggle */}
                  <Button
                    onClick={() => setIsPublicSharingEnabled(!isPublicSharingEnabled)}
                    style={{
                      background: isPublicSharingEnabled 
                        ? "linear-gradient(135deg, #27ae60, #2ecc71)" 
                        : "linear-gradient(135deg, #95a5a6, #7f8c8d)",
                      border: "none",
                      borderRadius: "20px",
                      padding: "8px 16px",
                      color: "white",
                      fontSize: "clamp(0.8rem, 2vw, 0.9rem)",
                      fontWeight: "600",
                      boxShadow: isPublicSharingEnabled 
                        ? "0 4px 15px rgba(39,174,96,0.3)" 
                        : "0 4px 15px rgba(149,165,166,0.3)",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      minWidth: "120px",
                      whiteSpace: "nowrap"
                    }}
                    title={isPublicSharingEnabled ? "Turn off public sharing" : "Turn on public sharing"}
                  >
                    <FontAwesomeIcon icon={isPublicSharingEnabled ? faToggleOn : faToggleOff} />
                    <span className="d-none d-sm-inline">
                      {isPublicSharingEnabled ? "Public ON" : "Public OFF"}
                    </span>
                    <span className="d-inline d-sm-none">
                      {isPublicSharingEnabled ? "ON" : "OFF"}
                    </span>
                  </Button>

                  {/* Share Public Button - Only show when sharing is enabled */}
                  {isPublicSharingEnabled && (
                    <Button
                      onClick={() => {
                        const publicUrl = `/live-marathon-public/${liveId}`;
                        window.open(publicUrl, '_blank');
                      }}
                      style={{
                        background: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
                        border: "none",
                        borderRadius: "20px",
                        padding: "8px 16px",
                        color: "white",
                        fontSize: "clamp(0.8rem, 2vw, 0.9rem)",
                        fontWeight: "600",
                        boxShadow: "0 4px 15px rgba(255,107,107,0.3)",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        minWidth: "120px",
                        whiteSpace: "nowrap"
                      }}
                      className="share-btn"
                    >
                      <FontAwesomeIcon icon={faShare} />
                      <span className="d-none d-sm-inline">Share Public</span>
                      <span className="d-inline d-sm-none">Share</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Stats Cards */}
        <Row className="g-3 g-md-4 mb-4">
          {/* Total Orders */}
          <Col xs={12} sm={6} lg={3}>
            <Card 
              style={{
                background: "rgba(255,255,255,0.95)",
                border: "none",
                borderRadius: "20px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                backdropFilter: "blur(10px)",
                height: "100%",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer"
              }}
              className="stat-card"
              onClick={() => handleCardClick('orders')}
            >
              <Card.Body className="text-center p-4">
                <div style={{
                  background: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  color: "white",
                  fontSize: "28px",
                  boxShadow: "0 8px 25px rgba(255,107,107,0.4)"
                }}>
                  <FontAwesomeIcon icon={faShoppingCart} />
                </div>
                <h2 style={{ 
                  color: "#2c3e50", 
                  margin: "0 0 8px 0",
                  fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
                  fontWeight: "800"
                }}>
                  {stats.totalOrders.toLocaleString()}
                </h2>
                <p style={{ 
                  color: "#7f8c8d", 
                  margin: 0,
                  fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
                  fontWeight: "600"
                }}>
                  Total Orders
                </p>
                <div style={{ 
                  marginTop: "10px",
                  color: "#27ae60",
                  fontSize: "0.9rem",
                  fontWeight: "600"
                }}>
                  +23% from yesterday
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Total Revenue */}
          <Col xs={12} sm={6} lg={3}>
            <Card 
              style={{
                background: "rgba(255,255,255,0.95)",
                border: "none",
                borderRadius: "20px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                backdropFilter: "blur(10px)",
                height: "100%",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer"
              }}
              className="stat-card"
              onClick={() => handleCardClick('revenue')}
            >
              <Card.Body className="text-center p-4">
                <div style={{
                  background: "linear-gradient(135deg, #2ecc71, #27ae60)",
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  color: "white",
                  fontSize: "28px",
                  boxShadow: "0 8px 25px rgba(46,204,113,0.4)"
                }}>
                  <FontAwesomeIcon icon={faDollarSign} />
                </div>
                <h2 style={{ 
                  color: "#2c3e50", 
                  margin: "0 0 8px 0",
                  fontSize: "clamp(1.3rem, 3vw, 1.8rem)",
                  fontWeight: "800"
                }}>
                  {formatCurrency(stats.totalRevenue)}
                </h2>
                <p style={{ 
                  color: "#7f8c8d", 
                  margin: 0,
                  fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
                  fontWeight: "600"
                }}>
                  Total Revenue
                </p>
                <div style={{ 
                  marginTop: "10px",
                  color: "#27ae60",
                  fontSize: "0.9rem",
                  fontWeight: "600"
                }}>
                  +18% from yesterday
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Total Stock */}
          <Col xs={12} sm={6} lg={3}>
            <Card 
              style={{
                background: "rgba(255,255,255,0.95)",
                border: "none",
                borderRadius: "20px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                backdropFilter: "blur(10px)",
                height: "100%",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer"
              }}
              className="stat-card"
              onClick={() => handleCardClick('stock')}
            >
              <Card.Body className="text-center p-4">
                <div style={{
                  background: "linear-gradient(135deg, #3498db, #2980b9)",
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  color: "white",
                  fontSize: "28px",
                  boxShadow: "0 8px 25px rgba(52,152,219,0.4)"
                }}>
                  <FontAwesomeIcon icon={faBoxes} />
                </div>
                <h2 style={{ 
                  color: "#2c3e50", 
                  margin: "0 0 8px 0",
                  fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
                  fontWeight: "800"
                }}>
                  {stats.totalStock.toLocaleString()}
                </h2>
                <p style={{ 
                  color: "#7f8c8d", 
                  margin: 0,
                  fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
                  fontWeight: "600"
                }}>
                  Items in Stock
                </p>
                <div style={{ 
                  marginTop: "10px",
                  color: "#e74c3c",
                  fontSize: "0.9rem",
                  fontWeight: "600"
                }}>
                  -12% from yesterday
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Live Duration */}
          <Col xs={12} sm={6} lg={3}>
            <Card 
              style={{
                background: "rgba(255,255,255,0.95)",
                border: "none",
                borderRadius: "20px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                backdropFilter: "blur(10px)",
                height: "100%",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer"
              }}
              className="stat-card"
              onClick={() => handleCardClick('duration')}
            >
              <Card.Body className="text-center p-4">
                <div style={{
                  background: "linear-gradient(135deg, #9b59b6, #8e44ad)",
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  color: "white",
                  fontSize: "28px",
                  boxShadow: "0 8px 25px rgba(155,89,182,0.4)"
                }}>
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <h2 style={{ 
                  color: "#2c3e50", 
                  margin: "0 0 8px 0",
                  fontSize: "clamp(1.5rem, 3vw, 2rem)",
                  fontWeight: "800",
                  fontFamily: "monospace"
                }}>
                  {getTimeDifference()}
                </h2>
                <p style={{ 
                  color: "#7f8c8d", 
                  margin: 0,
                  fontSize: "clamp(0.9rem, 2vw, 1.1rem)",
                  fontWeight: "600"
                }}>
                  Live Duration
                </p>
                <div style={{ 
                  marginTop: "10px",
                  color: "#f39c12",
                  fontSize: "0.9rem",
                  fontWeight: "600"
                }}>
                  🔥 Peak hours
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Secondary Stats Row */}
        <Row className="g-3 g-md-4 mb-4">
          <Col xs={12} sm={4}>
            <Card style={{
              background: "rgba(255,255,255,0.95)",
              border: "none",
              borderRadius: "15px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              backdropFilter: "blur(10px)",
              height: "100%"
            }}>
              <Card.Body className="text-center p-3">
                <FontAwesomeIcon icon={faChartLine} style={{ fontSize: "2rem", color: "#f39c12", marginBottom: "10px" }} />
                <h4 style={{ color: "#2c3e50", margin: "0 0 5px 0", fontWeight: "700" }}>
                  {stats.conversionRate}%
                </h4>
                <p style={{ color: "#7f8c8d", margin: 0, fontSize: "0.9rem" }}>
                  Conversion Rate
                </p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col xs={12} sm={4}>
            <Card style={{
              background: "rgba(255,255,255,0.95)",
              border: "none",
              borderRadius: "15px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              backdropFilter: "blur(10px)",
              height: "100%"
            }}>
              <Card.Body className="text-center p-3">
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: "2rem", color: "#e74c3c", marginBottom: "10px" }} />
                <h4 style={{ color: "#2c3e50", margin: "0 0 5px 0", fontWeight: "700" }}>
                  {formatNumber(stats.totalViews)}
                </h4>
                <p style={{ color: "#7f8c8d", margin: 0, fontSize: "0.9rem" }}>
                  Total Views
                </p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col xs={12} sm={4}>
            <Card style={{
              background: "rgba(255,255,255,0.95)",
              border: "none",
              borderRadius: "15px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              backdropFilter: "blur(10px)",
              height: "100%"
            }}>
              <Card.Body className="text-center p-3">
                <FontAwesomeIcon icon={faTrophy} style={{ fontSize: "2rem", color: "#f1c40f", marginBottom: "10px" }} />
                <h4 style={{ color: "#2c3e50", margin: "0 0 5px 0", fontWeight: "700" }}>
                  {formatCurrency(stats.avgOrderValue)}
                </h4>
                <p style={{ color: "#7f8c8d", margin: 0, fontSize: "0.9rem" }}>
                  Avg Order Value
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Orders & Live Details */}
        <Row className="g-3 g-md-4">
          {/* Recent Orders */}
          <Col xs={12} lg={8}>
            <Card style={{
              background: "rgba(255,255,255,0.95)",
              border: "none",
              borderRadius: "20px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
              backdropFilter: "blur(10px)"
            }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h4 style={{ 
                    color: "#2c3e50", 
                    margin: 0,
                    fontSize: "clamp(1.3rem, 3vw, 1.6rem)",
                    fontWeight: "700"
                  }}>
                    🛒 Recent Orders
                  </h4>
                  <Badge style={{
                    background: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
                    color: "white",
                    padding: "5px 12px",
                    borderRadius: "15px",
                    fontSize: "0.8rem"
                  }}>
                    Live Updates
                  </Badge>
                </div>
                
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {recentOrders.map((order, index) => (
                    <div key={order.id} style={{
                      padding: "15px",
                      marginBottom: "10px",
                      background: index % 2 === 0 ? "rgba(52,152,219,0.05)" : "rgba(46,204,113,0.05)",
                      borderRadius: "12px",
                      border: "1px solid rgba(0,0,0,0.05)"
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 style={{ margin: "0 0 5px 0", color: "#2c3e50", fontWeight: "600" }}>
                            {order.customerName}
                          </h6>
                          <p style={{ margin: "0 0 5px 0", color: "#7f8c8d", fontSize: "0.9rem" }}>
                            {order.product}
                          </p>
                          <small style={{ color: "#95a5a6" }}>{order.time}</small>
                        </div>
                        <div className="text-end">
                          <h6 style={{ margin: 0, color: "#27ae60", fontWeight: "700" }}>
                            {formatCurrency(order.amount)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Live Session Info */}
          <Col xs={12} lg={4}>
            <Card style={{
              background: "rgba(255,255,255,0.95)",
              border: "none",
              borderRadius: "20px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
              backdropFilter: "blur(10px)",
              height: "100%"
            }}>
              <Card.Body className="p-4">
                <h4 style={{ 
                  color: "#2c3e50", 
                  marginBottom: "25px",
                  fontSize: "clamp(1.3rem, 3vw, 1.6rem)",
                  fontWeight: "700"
                }}>
                  📊 Session Info
                </h4>
                
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faClock} style={{ color: "#3498db", marginRight: "10px" }} />
                    <div>
                      <strong style={{ color: "#2c3e50", fontSize: "0.95rem" }}>Start Time</strong>
                      <p style={{ margin: "2px 0 0 0", color: "#7f8c8d", fontSize: "0.85rem" }}>
                        {moment(liveData.createdAt).format("DD/MM/YYYY HH:mm")}
                      </p>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faEye} style={{ color: "#e74c3c", marginRight: "10px" }} />
                    <div>
                      <strong style={{ color: "#2c3e50", fontSize: "0.95rem" }}>Current Viewers</strong>
                      <p style={{ margin: "2px 0 0 0", color: "#7f8c8d", fontSize: "0.85rem" }}>
                        {liveData.viewers.toLocaleString()} watching
                      </p>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faHeart} style={{ color: "#e91e63", marginRight: "10px" }} />
                    <div>
                      <strong style={{ color: "#2c3e50", fontSize: "0.95rem" }}>Engagement</strong>
                      <p style={{ margin: "2px 0 0 0", color: "#7f8c8d", fontSize: "0.85rem" }}>
                        {liveData.likes} likes, {liveData.shares} shares
                      </p>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  borderRadius: "15px",
                  padding: "20px",
                  textAlign: "center",
                  color: "white"
                }}>
                  <h5 style={{ margin: "0 0 10px 0", fontWeight: "700" }}>🎯 Performance</h5>
                  <div style={{ fontSize: "2rem", fontWeight: "800", marginBottom: "5px" }}>
                    A+
                  </div>
                  <p style={{ margin: 0, fontSize: "0.9rem", opacity: 0.9 }}>
                    Excellent engagement rate!
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      
      {/* Full Screen Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        backdrop="static"
        style={{ zIndex: 9999 }}
      >
        <Modal.Body 
          style={{
            background: modalData.color || 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            textAlign: 'center',
            padding: '60px 40px',
            borderRadius: '20px',
            position: 'relative',
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Button
            variant="link"
            onClick={() => setShowModal(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              color: 'white',
              fontSize: '24px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>

          <div style={{
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            width: '120px',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px',
            fontSize: '48px'
          }}>
            {modalData.icon && <FontAwesomeIcon icon={modalData.icon} />}
          </div>

          <h1 style={{
            fontSize: 'clamp(2rem, 8vw, 6rem)',
            fontWeight: '900',
            margin: '0 0 20px 0',
            textShadow: '0 4px 8px rgba(0,0,0,0.3)',
            animation: 'pulse 2s infinite'
          }}>
            {modalData.value}
          </h1>

          <h3 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
            fontWeight: '600',
            margin: '0 0 40px 0',
            opacity: 0.9
          }}>
            {modalData.title}
          </h3>

          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '15px',
            padding: '20px 30px',
            fontSize: '1.2rem',
            fontWeight: '500'
          }}>
            🔥 Live Updates Every 3 Seconds
          </div>
        </Modal.Body>
      </Modal>

      {/* Audio elements for sound notifications */}
      <audio ref={orderSoundRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT" type="audio/wav" />
      </audio>
      <audio ref={revenueSoundRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT" type="audio/wav" />
      </audio>
      <audio ref={stockSoundRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT" type="audio/wav" />
      </audio>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        .stat-card:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 15px 50px rgba(0,0,0,0.2) !important;
        }
      `}</style>
    </div>
  );
}

export default LiveMarathon;